import { handleJsonResponse } from "./generic";

export const uploadMarketingArticle = (
  file,
  type,
  successCallback,
  errorCallback
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);
  return fetch("/api/marketing/articles", {
    method: "POST",
    body: formData,
  }).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

// Identifies one mounted editor so its edit lock can't be released or
// written under by another tab (see MarketingArticleLock on the backend).
const EDITOR_SESSION_HEADER = "X-Editor-Session";

export const acquireArticleLock = (
  articleId,
  sessionId,
  successCallback,
  errorCallback
) => {
  fetch(`/api/marketing/articles/${articleId}/lock`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId }),
  }).then((response) => {
    if (response.status === 423) {
      // Held by someone else: the second argument lets the editor open
      // read-only and name the holder instead of showing a bare error.
      return response
        .json()
        .then((data) => errorCallback(data.error, { lockedBy: data.locked_by }))
        .catch((error) => errorCallback(error));
    }
    return handleJsonResponse(response, successCallback, errorCallback);
  });
};

// `keepalive` lets the request outlive the page, so it still goes out when
// called from `pagehide` as the tab closes.
export const releaseArticleLock = (articleId, sessionId) => {
  fetch(`/api/marketing/articles/${articleId}/lock`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId }),
    keepalive: true,
  }).catch(() => {});
};

export const uploadArticleImage = (
  articleId,
  sessionId,
  file,
  successCallback,
  errorCallback
) => {
  const formData = new FormData();
  formData.append("file", file);
  return fetch(`/api/marketing/articles/${articleId}/images`, {
    method: "POST",
    headers: { [EDITOR_SESSION_HEADER]: sessionId },
    body: formData,
  }).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

export const importArticle = (type, slug, successCallback, errorCallback) => {
  fetch("/api/marketing/articles/import", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type, slug }),
  }).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

export const fetchArticleMeta = (successCallback, errorCallback) => {
  fetch("/api/marketing/articles/meta").then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

export const fetchArticleContent = (
  articleId,
  successCallback,
  errorCallback
) => {
  fetch(`/api/marketing/articles/${articleId}/content`).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

export const updateArticle = (
  articleId,
  sessionId,
  frontmatter,
  body,
  successCallback,
  errorCallback
) => {
  fetch(`/api/marketing/articles/${articleId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      [EDITOR_SESSION_HEADER]: sessionId,
    },
    body: JSON.stringify({ frontmatter, body }),
  }).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

// Validates the editor's unsaved draft without saving it. With `autofix`, the
// automatic fixes are applied first and the response carries the fixed body.
export const validateArticleDraft = (
  articleId,
  frontmatter,
  body,
  autofix,
  successCallback,
  errorCallback
) => {
  fetch(`/api/marketing/articles/${articleId}/validate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ frontmatter, body, autofix }),
  }).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

export const fetchRedirects = (successCallback, errorCallback) => {
  fetch("/api/marketing/redirects").then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

export const createRedirect = (
  source,
  destination,
  successCallback,
  errorCallback
) => {
  fetch("/api/marketing/redirects", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source, destination }),
  }).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

export const updateRedirect = (
  redirectId,
  fields,
  successCallback,
  errorCallback
) => {
  fetch(`/api/marketing/redirects/${redirectId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fields),
  }).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

export const deleteRedirect = (redirectId, successCallback, errorCallback) => {
  fetch(`/api/marketing/redirects/${redirectId}`, {
    method: "DELETE",
  }).then((response) => {
    if (response.ok) {
      successCallback();
    } else {
      handleJsonResponse(response, successCallback, errorCallback);
    }
  });
};

export const refreshRedirects = (successCallback, errorCallback) => {
  fetch("/api/marketing/redirects/refresh", {
    method: "POST",
  }).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};

export const publishRedirects = (
  commitMessage,
  successCallback,
  errorCallback
) => {
  fetch("/api/marketing/redirects/publish", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ commit_message: commitMessage }),
  }).then((response) =>
    handleJsonResponse(response, successCallback, errorCallback)
  );
};
